import React, { useEffect, useState } from "react";

import { useLazyQuery } from "@apollo/client";
// import { Link } from "gatsby";

import PromoBlock from "@/components/common/promoBlock";
import ScrollToTopButton from "@/components/common/scrollToTopButton";
import { SITE_DATE_TIME_ZONE } from "@/constants";
import { CarouselSeoUrlPrefixEnum } from "@/globalTypes";
import { useBanners } from "@/hooks/useBanners";
import useTextContents from "@/hooks/useTextContents";
import { convertToShortDate } from "@/utils/dateUtils";
import { cleanupFromDangerousTags } from "@/utils/stringUtils";
import Breadcrumbs from "@components/common/Breadcrumbs";
import { BreadcrumbUrlPrefixEnum } from "@components/common/Breadcrumbs/types";
// import ExpertStatus from "@components/common/expertStatus";
import { Icon, IconSizeEnum, IconTypeEnum } from "@components/common/icon";
// import Rating from "@components/common/rating";
import SeoHeaders from "@components/common/seoHeaders";

import CarouselLinksBlock from "../carouselLinksBlock";
import Constructor from "../constructor";
import { getFirstScreenZone } from "../constructor/utils";
import Persons from "../persons";
import { getArticlePageNodes, getFirstContentImageUrl, getReadDurationMinByText } from "../utils";

import { MIN_UPDATE_DIFFERENCE_MS } from "./constants";
import { getExpertStateForLifestyle, getExpertStateForLifestyleVariables } from "./graphql/__generated__/getExpertStateForLifestyle";
import { GET_EXPERT_STATE_FOR_LIFESTYLE } from "./graphql/GET_EXPERT_STATE_FOR_LIFESTYLE";
import { LifestyleProps } from "./types";

import "../styles.scss";
import "./styles.scss";

const Lifestyle = ({ pageContext }: LifestyleProps) => {
  const {
    seoId,
    title = "Статья на Lunaro",
    ogTitle,
    description = "Статья на Lunaro",
    text,
    textMiddle,
    textBottom,
    header,
    published,
    updated,
    coverImage,
    breadcrumbDisplayName,
    breadcrumbs,
    url,
    author,
    expert,
    autolinkedArticleLinks,
    zone,
    commonTextTopBottomZone,
    commonTextMiddleBottomZone,
    commonBottomZone,
    expertsInitial,
    wideBannerData,
    isContentsHidden,
  } = pageContext;
  const {
    HeadingsLayout,
    convertedTextBottom,
    convertedTextMiddle,
    convertedTextTop } = useTextContents({ textTop: text, textMiddle, textBottom, isContentsHidden });
  // картинку первого экрана получает только зона, выше которой нет длинного текста и других блоков
  const firstScreenZone = getFirstScreenZone([
    text,
    commonTextTopBottomZone,
    textMiddle,
    commonTextMiddleBottomZone,
    textBottom,
    zone,
  ]);
  const { checkIsWideBannerActive } = useBanners();

  const publishDateTimestamp = new Date(published).getTime();
  const updatedTimestamp = new Date(updated).getTime();
  const publishDate = convertToShortDate(publishDateTimestamp, SITE_DATE_TIME_ZONE);
  const updateDate = updatedTimestamp - publishDateTimestamp >= MIN_UPDATE_DIFFERENCE_MS
    ? convertToShortDate(new Date(updated).getTime(), SITE_DATE_TIME_ZONE)
    : null;
  const minToRead = getReadDurationMinByText(
    `${text ?? ""} ${textMiddle ?? ""} ${textBottom ?? ""}`,
  );

  const contentImageUrl = getFirstContentImageUrl([text, textMiddle, textBottom]);
  // Граф Schema.org статьи (4559): те же заголовок, даты, автор и картинка, что видит читатель.
  const articlePageNodes = getArticlePageNodes({
    url,
    headline: header || title,
    description,
    contentImageUrl,
    coverImage,
    published,
    updated,
    author,
  });

  const [expertStatus, setExpertStatus] = useState(expert?.state);

  const [getExpertState, { data, error, loading }] = useLazyQuery<
    getExpertStateForLifestyle,
    getExpertStateForLifestyleVariables
  >(GET_EXPERT_STATE_FOR_LIFESTYLE, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (expert) {
      getExpertState({ variables: { id: expert.id } });
    }
  }, [expert, getExpertState]);

  useEffect(() => {
    if (data && !error && !loading) {
      setExpertStatus(data.getExpert.state);
    }
  }, [data, error, loading]);

  const wideBannerBannerIsShown = wideBannerData && checkIsWideBannerActive({
    isShown: wideBannerData.wideBannerIsShownOnArticle,
    isShownForUnauthorized: wideBannerData.wideBannerIsShownForUnauthorized,
    isShownForAuthorizedWithTrial: wideBannerData.wideBannerIsShownForAuthorizedWithTrial,
    isShownForAuthorizedWithoutTrial: wideBannerData.wideBannerIsShownForAuthorizedWithoutTrial,
  });

  return (
    <>
      <SeoHeaders
        title={title}
        ogTitle={ogTitle}
        description={description}
        url={url}
        imageUrl={contentImageUrl}
        pageNodes={articlePageNodes}
      />
      {wideBannerBannerIsShown && <PromoBlock wideBannerData={wideBannerData} />}
      <ScrollToTopButton />
      <div className="bg--white">
        <div className="page-width">
          <Breadcrumbs
            seoId={seoId}
            url={url}
            breadcrumbDisplayName={breadcrumbDisplayName}
            breadcrumbs={breadcrumbs}
            urlPrefix={BreadcrumbUrlPrefixEnum.Lifestyles}
          />
          <div className="seo-lifestyle">
            <h1 className="seo-lifestyle__headline">
              {header || ""}
            </h1>
            <div className="seo-lifestyle__data">
              <div className="seo-lifestyle__data-item">
                {publishDate}
              </div>
              {updateDate && (
                <div className="seo-lifestyle__data-item">
                  Обновлено
                  {" "}
                  {updateDate}
                </div>
              )}
              <div className="seo-lifestyle__data-item">
                <Icon type={IconTypeEnum.Time} size={IconSizeEnum.Size20} />
                <div>
                  {minToRead}
                  {" "}
                  мин на&nbsp;чтение
                </div>
              </div>
            </div>
            <HeadingsLayout />
            <div
              className="seo-typography"
              dangerouslySetInnerHTML={{
                __html: cleanupFromDangerousTags(convertedTextTop || ""),
              }}
            />
          </div>
          <Constructor
            zone={commonTextTopBottomZone}
            expertsInitial={expertsInitial}
            isImagePriorityAllowed={commonTextTopBottomZone === firstScreenZone}
          />
          <div
            className="seo-typography"
            dangerouslySetInnerHTML={{
              __html: cleanupFromDangerousTags(convertedTextMiddle || ""),
            }}
          />
          <Constructor
            zone={commonTextMiddleBottomZone}
            expertsInitial={expertsInitial}
            isImagePriorityAllowed={commonTextMiddleBottomZone === firstScreenZone}
          />
          <div
            className="seo-typography"
            dangerouslySetInnerHTML={{
              __html: cleanupFromDangerousTags(convertedTextBottom || ""),
            }}
          />
          <Constructor
            zone={zone}
            expertsInitial={expertsInitial}
            isImagePriorityAllowed={zone === firstScreenZone}
          />
          <Persons author={author} expert={expert} />
          {autolinkedArticleLinks?.length > 0 && (
            <div className="seo-lifestyle__carousel-block-container">
              <h2>Читайте также</h2>
              <CarouselLinksBlock
                autolinkedArticleLinks={autolinkedArticleLinks}
                urlPrefix={CarouselSeoUrlPrefixEnum.Article}
                seoId={seoId}
              />
            </div>
          )}
          <Constructor zone={commonBottomZone} expertsInitial={expertsInitial} isImagePriorityAllowed={false} />
        </div>
      </div>
    </>
  );
};

export default Lifestyle;
